import Anchor from "@components/Anchor";
import Form from "@components/Form";
import Modal from "@components/Modal";
import {
	colours,
	device,
	getColours,
	isEmptyContentfulEntry,
	richTextHelper,
} from "@helpers";
import type { IModuleFormFields } from "@types-contentful";
import React, { useState } from "react";
import styled from "styled-components";
import CardWrapper from "./CardLayout";

const Container = styled(CardWrapper)<{
	backgroundColor:
		| "Midnight Blue"
		| "White"
		| "Peach"
		| "Pale Peach"
		| "Watermelon"
		| "Mint Green"
		| "Green"
		| undefined;
}>`
  background-color: ${({ backgroundColor }) => getColours(backgroundColor)};
  border-top-left-radius: 60px;
  box-shadow: 0 -5px 30px -5px rgba(18, 18, 82, 0.15);
  display: flex;
  flex-direction: column;
  margin-top: -47px;
  padding: 30px 38px 49px;
  position: relative;
  width: 100%;
  & h2 {
  }
  & p {
  }
  & span {
  }
  & label {
  }
  @media (min-width: ${device.mobileM}) {
    margin-top: -61px;
  }
  @media (min-width: ${device.tablet}) and (max-width: ${device.laptop}) {
    display: block;
    padding: 81px 58px 64px 64px;
  }

  @media (min-width: ${device.laptop}) {
    margin-top: -100px;
  }
`;

const FormHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  @media (min-width: ${device.laptop}) and (max-width: ${device.desktopS}) {
    padding: 42px 141px 0px 70px;
  }

  @media (min-width: ${device.desktopS}) and (max-width: ${device.desktop}) {
    padding: 40px 168px 0px 95px;
  }

  @media (min-width: ${device.desktop}) {
    padding: 40px 168px 0px 416px;
  }
`;

const FormSubheader = styled.div`
  margin-top: 5px;

  @media (min-width: ${device.laptop}) and (max-width: ${device.desktopS}) {
    margin-top: 20px;
  }

  @media (min-width: ${device.desktopS}) {
    margin-top: 43px;
  }
`;

const ContentfulModuleForm = ({
	formType,
	name,
	smallprint,
	submitButtonText,
	subtext,
	anchor,
	backgroundColor,
	headline,
}: IModuleFormFields) => {
	const [showModal, setShowModal] = useState(false);

	const closeModal = () => setShowModal(false);

	const openModal = () => setShowModal(true);

	const renderFormHeaderLine = () => (
		<FormHeaderContainer>
			{!isEmptyContentfulEntry(headline) && <h2>{headline}</h2>}
			{!isEmptyContentfulEntry(subtext) && (
				<FormSubheader className="large">
					{richTextHelper(subtext)}
				</FormSubheader>
			)}
		</FormHeaderContainer>
	);

	const renderForm = () => (
		<Form
			formType={formType}
			footerText={smallprint}
			// @ts-ignore
			closeModal={closeModal}
			openModal={openModal}
			submitButtonText={submitButtonText}
			formName={name}
		/>
	);

	const renderModal = () => {
		switch (formType) {
			case "register-your-interest":
				return (
					// @ts-ignore
					<Modal
						showModal={showModal}
						closeModal={closeModal}
						body={[
							"Thanks for registering your interest in felix. Please check your inbox to confirm your email address.",
							<br key="br-1" />,
							<br key="br-2" />,
							"We’ll be in touch to share our official launch details and date (coming soon!).",
							<br key="br-3" />,
							<br key="br-4" />,
							"We can’t wait for you to join us.",
						]}
					/>
				);
			case "remind-me-later": {
				return (
					<Modal
						showModal={showModal}
						closeModal={closeModal}
						heading="Thank you for leaving your details."
						body={[
							"We will contact you before your existing plan is ready for renewal.",
							<br key="br-5" />,
							<br key="br-6" />,
							"So you can switch to felix and help us do some good!",
						]}
					/>
				);
			}

			case "friends-family": {
				return (
					<Modal
						showModal={showModal}
						closeModal={closeModal}
						heading="Done!"
						body={[
							"The unique promo code has been emailed to your friend’s inbox.",
						]}
					/>
				);
			}

			case "lead-collection-form": {
				return (
					<Modal
						showModal={showModal}
						closeModal={closeModal}
						heading="Done!"
						body={["Thanks for providing your details"]}
					/>
				);
			}
			case "sms-me-link": {
				return (
					<Modal
						showModal={showModal}
						closeModal={closeModal}
						heading="You should receive an SMS from us any second."
						body={[
							"Please check your mobile phone for a new SMS with a the email verification link",
						]}
					/>
				);
			}
			default:
				return null;
		}
	};

	return (
		<>
			<Container backgroundColor={backgroundColor}>
				{!isEmptyContentfulEntry(anchor) && (
					<Anchor name={anchor} href={undefined} />
				)}
				{renderFormHeaderLine()}
				{renderForm()}
				{renderModal()}
			</Container>
		</>
	);
};

export default ContentfulModuleForm;
